import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SuperAdminLayout from '../components/SuperAdminLayout';
import { getAllPlans, updatePlan } from '../services/subscriptionPlanService';

/**
 * Format currency
 */
const formatCurrency = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '-';
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  });
  return formatter.format(amount);
};

/**
 * Format date
 */
const formatDate = (dateValue) => {
  if (!dateValue) return '-';

  try {
    let date;

    // Handle array format from backend: [year, month, day, hour, minute, second]
    if (Array.isArray(dateValue) && dateValue.length >= 6) {
      const [year, month, day, hour, minute, second] = dateValue;
      date = new Date(year, month - 1, day, hour, minute, second || 0);
    }
    // Handle string format
    else if (typeof dateValue === 'string') {
      let isoDateString = dateValue;
      if (dateValue.includes(' ') && !dateValue.includes('T')) {
        isoDateString = dateValue.replace(' ', 'T');
      }
      date = new Date(isoDateString);
    }
    // Handle Date object
    else if (dateValue instanceof Date) {
      date = dateValue;
    }
    else {
      return '-';
    }

    if (isNaN(date.getTime())) {
      return '-';
    }

    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  } catch (error) {
    console.error('Error formatting date:', error, dateValue);
    return '-';
  }
};

/**
 * Format unlimited value
 */
const formatLimit = (value) => {
  if (value === null || value === undefined || value === -1) {
    return 'Unlimited';
  }
  return value.toLocaleString();
};

/**
 * Expandable Plan Row Component
 */
const PlanRow = ({ plan, onEdit, onToggleStatus }) => {
  const [expanded, setExpanded] = useState(false);

  const {
    id,
    planCode,
    name,
    description,
    priceMonthly,
    priceYearly,
    currency,
    trialDays,
    maxUsers,
    maxLocations,
    maxProducts,
    maxMonthlyOrders,
    maxStorageGb,
    features,
    isPopular,
    sortOrder,
    status,
    createdAt,
    updatedAt,
    yearlyDiscountPercentage,
  } = plan;

  // Calculate yearly discount
  const discount = yearlyDiscountPercentage 
    ? parseFloat(yearlyDiscountPercentage).toFixed(1)
    : priceMonthly && priceYearly
    ? ((1 - (parseFloat(priceYearly) / (parseFloat(priceMonthly) * 12))) * 100).toFixed(1)
    : 0;

  // Count enabled features (only our three plan flags)
  const FEATURE_KEYS = ['loyalty_points', 'whatsapp_bill_share', 'ecommerce_website'];

  let enabledFeatures = 0;
  let totalFeatures = FEATURE_KEYS.length;

  if (!features || Object.keys(features).length === 0) {
    // Existing/legacy plans without explicit flags: treat as all features enabled
    enabledFeatures = FEATURE_KEYS.length;
  } else {
    enabledFeatures = FEATURE_KEYS.filter((key) => features[key] === true).length;
  }

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {planCode}
            </Typography>
            {isPopular && (
              <Tooltip title="Popular Plan">
                <StarIcon sx={{ fontSize: 18, color: '#ff9800' }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {name}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {formatCurrency(priceMonthly, currency)} / month
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatCurrency(priceYearly, currency)} / year
          </Typography>
          {discount > 0 && (
            <Chip 
              label={`Save ${discount}%`} 
              size="small" 
              color="success" 
              sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
            />
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={status || 'ACTIVE'}
            size="small"
            color={status === 'ACTIVE' ? 'success' : status === 'INACTIVE' ? 'default' : 'error'}
            sx={{ fontSize: '0.7rem' }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {trialDays || 0} days
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {enabledFeatures} / {totalFeatures} features
          </Typography>
        </TableCell>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => onEdit(plan)}
            sx={{ color: '#5a9bd4' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <Switch
            checked={(status || 'ACTIVE').toUpperCase() === 'ACTIVE'}
            onChange={(e) => onToggleStatus(plan, e.target.checked)}
            sx={{ ml: 1 }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                        Basic Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Description</Typography>
                          <Typography variant="body2">{description || '-'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Currency</Typography>
                          <Typography variant="body2">{currency || 'USD'}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Sort Order</Typography>
                          <Typography variant="body2">{sortOrder || 0}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Usage Limits */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                        Usage Limits
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Max Users</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatLimit(maxUsers)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Max Locations</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatLimit(maxLocations)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Max Products</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatLimit(maxProducts)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Max Monthly Orders</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatLimit(maxMonthlyOrders)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption" color="text.secondary">Max Storage</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatLimit(maxStorageGb)} {maxStorageGb !== -1 && 'GB'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Features */}
                {features && Object.keys(features).length > 0 && (
                  <Grid item xs={12}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
                          Features ({enabledFeatures} enabled)
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Object.entries(features).map(([key, value]) => (
                            <Chip
                              key={key}
                              label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              size="small"
                              color={value ? 'primary' : 'default'}
                              variant={value ? 'filled' : 'outlined'}
                              icon={value ? <CheckCircleIcon /> : <CancelIcon />}
                            />
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * Plan Card Component for Grid View
 */
const PlanCard = ({ plan, onEdit, onToggleStatus }) => {
  const {
    planCode,
    name,
    description,
    priceMonthly,
    priceYearly,
    currency,
    trialDays,
    maxUsers,
    maxLocations,
    maxProducts,
    maxMonthlyOrders,
    maxStorageGb,
    features,
    isPopular,
    status,
  } = plan;

  // Calculate yearly discount
  const discount = priceMonthly && priceYearly
    ? ((1 - (parseFloat(priceYearly) / (parseFloat(priceMonthly) * 12))) * 100).toFixed(1)
    : 0;

  // Count enabled features
  const enabledFeatures = features ? Object.keys(features).filter(key => features[key]).length : 0;
  const totalFeatures = features ? Object.keys(features).length : 0;

  return (
    <Card 
      elevation={2} 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: isPopular ? '2px solid #ff9800' : '1px solid #e0e0e0',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-4px)',
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
                {name}
              </Typography>
              {isPopular && (
                <Tooltip title="Popular Plan">
                  <StarIcon sx={{ fontSize: 20, color: '#ff9800' }} />
                </Tooltip>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {planCode}
            </Typography>
          </Box>
          <Chip
            label={status || 'ACTIVE'}
            size="small"
            color={status === 'ACTIVE' ? 'success' : status === 'INACTIVE' ? 'default' : 'error'}
            sx={{ fontSize: '0.7rem' }}
          />
        </Box>

        {/* Description */}
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
            {description.length > 80 ? `${description.substring(0, 80)}...` : description}
          </Typography>
        )}

        {/* Pricing */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#5a9bd4', mb: 0.5 }}>
            {formatCurrency(priceMonthly, currency)}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
              /month
            </Typography>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatCurrency(priceYearly, currency)} / year
          </Typography>
          {discount > 0 && (
            <Chip 
              label={`Save ${discount}%`} 
              color="success" 
              size="small" 
              sx={{ mt: 1 }}
            />
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Quick Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Trial Period</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {trialDays || 0} days
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Features</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {enabledFeatures} / {totalFeatures}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Max Users</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatLimit(maxUsers)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Max Locations</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatLimit(maxLocations)}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => onEdit(plan)}
          sx={{ color: '#5a9bd4' }}
        >
          Edit
        </Button>
        <Switch
          checked={(status || 'ACTIVE').toUpperCase() === 'ACTIVE'}
          onChange={(e) => onToggleStatus(plan, e.target.checked)}
          sx={{ ml: 1 }}
        />
      </CardActions>
    </Card>
  );
};

/**
 * Subscriptions Page
 * Display all subscription plans
 */
const SubscriptionsPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setView] = useState('list'); // 'list' or 'grid'

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const plansData = await getAllPlans();
      setPlans(plansData);
    } catch (err) {
      console.error('Error loading plans:', err);
      setError(err.message || 'Failed to load subscription plans');
      enqueueSnackbar(err.message || 'Failed to load subscription plans', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (plan) => {
    navigate(`/superadmin/subscriptions/edit/${plan.id}`);
  };

  const handleCreate = () => {
    navigate('/superadmin/subscriptions/create');
  };
  
  const handleToggleStatus = async (plan, isActive) => {
    if (!plan) return;
    const nextStatus = isActive ? 'ACTIVE' : 'INACTIVE';
    try {
      await updatePlan(plan.id, { status: nextStatus });
      alert(nextStatus === 'ACTIVE' ? 'Plan activated.' : 'Plan inactivated.');
      await loadPlans();
    } catch (err) {
      alert(err.message || 'Failed to update plan status');
    }
  };

  return (
    <SuperAdminLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#2c3e50' }}>
              All Plans
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage all subscription plans
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* View Toggle */}
            <ToggleButtonGroup
              value={view}
              exclusive
              onChange={(e, newView) => {
                if (newView !== null) {
                  setView(newView);
                }
              }}
              size="small"
              sx={{ height: 36 }}
            >
              <ToggleButton value="list" aria-label="list view">
                <ViewListIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="grid" aria-label="grid view">
                <ViewModuleIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ bgcolor: '#5a9bd4', '&:hover': { bgcolor: '#4a8bc4' } }}
            >
              Create Plan
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Plans Display */}
        {loading ? (
          <Paper elevation={2} sx={{ borderRadius: 2, p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress />
            </Box>
          </Paper>
        ) : plans.length === 0 ? (
          <Paper elevation={2} sx={{ borderRadius: 2, p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Plans Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Get started by creating your first subscription plan.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ bgcolor: '#5a9bd4', '&:hover': { bgcolor: '#4a8bc4' } }}
            >
              Create Plan
            </Button>
          </Paper>
        ) : view === 'list' ? (
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ width: 50 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Plan Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Pricing</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Trial</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Features</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plans.map((plan) => (
                    <PlanRow key={plan.id} plan={plan} onEdit={handleEdit} onToggleStatus={handleToggleStatus} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {plans.map((plan) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={plan.id}>
                <PlanCard plan={plan} onEdit={handleEdit} onToggleStatus={handleToggleStatus} />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </SuperAdminLayout>
  );
};

export default SubscriptionsPage;
